import {
  contractLabel,
  formatDisplayDate,
  professionalLabel,
  STICKY_END_CELL,
  STICKY_END_HEADER,
  WIDE_TABLE_CONTAINER,
} from '@features/allocation';
import {
  Badge,
  Button,
  EmptyState,
  IconButton,
  Table,
  type TableSortDirection,
} from '@ntconsult/aluma-react';
import { Check, Minus, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ProjectColumnsState } from '../hooks/useProjectColumns';
import type { ProjectFilterState, ProjectSearchKey } from '../hooks/useProjectFilters';
import { useProjectStatusLabel } from '../hooks/useProjectStatusLabel';
import {
  PROJECT_STATUS_IDS,
  PROJECT_UNITS,
  type ProjectManager,
  type ProjectRow,
  type ProjectSortField,
} from '../types/ProjectList';

type ProjectListTableProps = {
  items: ProjectRow[];
  filterState: ProjectFilterState;
  columnsState: ProjectColumnsState;
  /** Só admin vê a coluna de ações (editar). */
  isAdmin: boolean;
  onEdit: (project: ProjectRow) => void;
};

const STATUS_TONE = {
  0: 'success',
  1: 'info',
  2: 'danger',
  3: 'neutral',
  4: 'warning',
} as const;

const managerText = (manager: ProjectManager | null) =>
  manager ? professionalLabel(manager) : '—';

/**
 * Colunas e filtros do `ProjetoHeaderList.php` do legado. Só leitura: o cadastro vem do Access BR/US.
 * Ocultar uma coluna não remove o filtro dela: o chip continua.
 */
export function ProjectListTable({
  items,
  filterState,
  columnsState,
  isAdmin,
  onEdit,
}: ProjectListTableProps) {
  const { t, i18n } = useTranslation(['projetos', 'alocacao', 'common']);
  const statusLabel = useProjectStatusLabel();
  const { filters, setSort, setSearch, setUnit, setStatuses, setFlag } = filterState;
  const { isVisible, visibleColumns } = columnsState;
  // +2: "Cód. Axis" e "Nome", sempre visíveis; +1 pela coluna de ações do admin.
  const columnCount = visibleColumns.length + 2 + (isAdmin ? 1 : 0);

  const sortProps = (field: ProjectSortField) => ({
    sortable: true,
    sortDirection: (filters.sortBy === field
      ? filters.sortDirection
      : 'none') as TableSortDirection,
    onSortChange: (next: TableSortDirection) => setSort(field, next),
  });

  const textFilter = (key: ProjectSearchKey, label: string) => (
    <Table.TextFilter
      label={label}
      value={filters[key] ?? ''}
      onValueChange={(value) => setSearch(key, value)}
    />
  );

  const yesNoOptions = [
    { value: 'true', label: t('common:yes') },
    { value: 'false', label: t('common:no') },
  ];

  const flagFilter = (key: 'internal' | 'pool', label: string) => (
    <Table.OptionsFilter
      label={label}
      options={yesNoOptions}
      value={filters[key] === undefined ? [] : [String(filters[key])]}
      // Sim e Não marcados juntos = sem filtro.
      onValueChange={(values) => setFlag(key, values.length === 1 ? values[0] === 'true' : null)}
    />
  );

  const yesNoBadge = (value: boolean) =>
    value ? (
      <Badge variant="soft" tone="success" shape="pill" size="sm" icon={<Check size={12} />}>
        {t('common:yes')}
      </Badge>
    ) : (
      <Badge variant="soft" tone="danger" shape="pill" size="sm" icon={<Minus size={12} />}>
        {t('common:no')}
      </Badge>
    );

  return (
    <Table density="compact" stickyHeader containerClassName={WIDE_TABLE_CONTAINER}>
      <Table.Caption className="sr-only">{t('projetos:titulo')}</Table.Caption>
      <Table.Head>
        <Table.Row>
          {isVisible('unit') && (
            <Table.Header
              {...sortProps('unit')}
              filter={
                <Table.OptionsFilter
                  label={t('projetos:colunas.unidade')}
                  options={PROJECT_UNITS.map((unit) => ({
                    value: String(unit.id),
                    label: unit.code,
                  }))}
                  value={filters.unitId === undefined ? [] : [String(filters.unitId)]}
                  // Todas as unidades marcadas = sem filtro.
                  onValueChange={(values) =>
                    setUnit(values.length === 1 ? Number(values[0]) : null)
                  }
                />
              }
            >
              {t('projetos:colunas.unidade')}
            </Table.Header>
          )}
          {isVisible('contract') && (
            <Table.Header
              {...sortProps('contract')}
              filter={textFilter('contractSearch', t('projetos:colunas.contrato'))}
            >
              {t('projetos:colunas.contrato')}
            </Table.Header>
          )}
          <Table.Header
            {...sortProps('axisCode')}
            filter={textFilter('axisCode', t('projetos:colunas.codigo'))}
          >
            {t('projetos:colunas.codigo')}
          </Table.Header>
          <Table.Header
            {...sortProps('name')}
            filter={textFilter('name', t('projetos:colunas.nome'))}
          >
            {t('projetos:colunas.nome')}
          </Table.Header>
          {isVisible('client') && (
            <Table.Header filter={textFilter('clientSearch', t('projetos:colunas.cliente'))}>
              {t('projetos:colunas.cliente')}
            </Table.Header>
          )}
          {isVisible('status') && (
            <Table.Header
              {...sortProps('status')}
              filter={
                <Table.OptionsFilter
                  label={t('projetos:colunas.situacao')}
                  options={PROJECT_STATUS_IDS.map((id) => ({
                    value: String(id),
                    label: statusLabel(id),
                  }))}
                  value={(filters.statusIds ?? []).map(String)}
                  onValueChange={(values) => setStatuses(values.map(Number))}
                />
              }
            >
              {t('projetos:colunas.situacao')}
            </Table.Header>
          )}
          {isVisible('startDate') && (
            <Table.Header {...sortProps('startDate')}>{t('projetos:colunas.inicio')}</Table.Header>
          )}
          {isVisible('endDate') && (
            <Table.Header {...sortProps('endDate')}>{t('projetos:colunas.termino')}</Table.Header>
          )}
          {isVisible('projectManager') && (
            <Table.Header
              {...sortProps('projectManager')}
              filter={textFilter('projectManagerSearch', t('projetos:colunas.gerenteProjeto'))}
            >
              {t('projetos:colunas.gerenteProjeto')}
            </Table.Header>
          )}
          {isVisible('operationManager') && (
            <Table.Header
              {...sortProps('operationManager')}
              filter={textFilter('operationManagerSearch', t('projetos:colunas.gerenteOperacao'))}
            >
              {t('projetos:colunas.gerenteOperacao')}
            </Table.Header>
          )}
          {isVisible('accountDirector') && (
            <Table.Header
              {...sortProps('accountDirector')}
              filter={textFilter('accountDirectorSearch', t('projetos:colunas.diretorConta'))}
            >
              {t('projetos:colunas.diretorConta')}
            </Table.Header>
          )}
          {isVisible('internal') && (
            <Table.Header
              align="center"
              {...sortProps('internal')}
              filter={flagFilter('internal', t('projetos:colunas.interno'))}
            >
              {t('projetos:colunas.interno')}
            </Table.Header>
          )}
          {isVisible('pool') && (
            <Table.Header
              align="center"
              {...sortProps('pool')}
              filter={flagFilter('pool', t('projetos:colunas.pool'))}
            >
              {t('projetos:colunas.pool')}
            </Table.Header>
          )}
          {isAdmin && (
            <Table.Header align="right" className={STICKY_END_HEADER}>
              {t('projetos:edicao.acoes')}
            </Table.Header>
          )}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {items.length === 0 ? (
          // Vazio por filtro mantém o cabeçalho: é nele que o filtro se ajusta.
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <EmptyState
                title={t('projetos:tabela.filtroSemResultadoTitulo')}
                description={t('projetos:tabela.filtroSemResultadoDescricao')}
                action={
                  <Button variant="secondary" onClick={filterState.clearFilters}>
                    {t('alocacao:filtros.limpar')}
                  </Button>
                }
              />
            </Table.Cell>
          </Table.Row>
        ) : (
          items.map((project) => (
            <Table.Row key={project.id}>
              {isVisible('unit') && <Table.Cell>{project.unit.code}</Table.Cell>}
              {isVisible('contract') && (
                <Table.Cell>{project.contract ? contractLabel(project.contract) : '—'}</Table.Cell>
              )}
              <Table.Cell className="whitespace-nowrap tabular-nums">{project.axisCode}</Table.Cell>
              <Table.Cell className="min-w-50">{project.name}</Table.Cell>
              {isVisible('client') && <Table.Cell>{project.client?.name ?? '—'}</Table.Cell>}
              {isVisible('status') && (
                <Table.Cell>
                  <Badge
                    variant="soft"
                    tone={STATUS_TONE[project.status as keyof typeof STATUS_TONE] ?? 'neutral'}
                    shape="pill"
                    size="sm"
                  >
                    {statusLabel(project.status)}
                  </Badge>
                </Table.Cell>
              )}
              {isVisible('startDate') && (
                <Table.Cell className="whitespace-nowrap tabular-nums">
                  {project.startDate ? formatDisplayDate(project.startDate, i18n.language) : '—'}
                </Table.Cell>
              )}
              {isVisible('endDate') && (
                <Table.Cell className="whitespace-nowrap tabular-nums">
                  {project.endDate ? formatDisplayDate(project.endDate, i18n.language) : '—'}
                </Table.Cell>
              )}
              {isVisible('projectManager') && (
                <Table.Cell>{managerText(project.projectManager)}</Table.Cell>
              )}
              {isVisible('operationManager') && (
                <Table.Cell>{managerText(project.operationManager)}</Table.Cell>
              )}
              {isVisible('accountDirector') && (
                <Table.Cell>{managerText(project.accountDirector)}</Table.Cell>
              )}
              {isVisible('internal') && (
                <Table.Cell align="center">{yesNoBadge(project.isInternal)}</Table.Cell>
              )}
              {isVisible('pool') && (
                <Table.Cell align="center">{yesNoBadge(project.isPool)}</Table.Cell>
              )}
              {isAdmin && (
                <Table.Cell align="right" className={STICKY_END_CELL}>
                  <IconButton
                    icon={<Pencil size={14} />}
                    label={t('projetos:edicao.editarProjeto', { nome: project.name })}
                    variant="icon"
                    size="sm"
                    onClick={() => onEdit(project)}
                  />
                </Table.Cell>
              )}
            </Table.Row>
          ))
        )}
      </Table.Body>
    </Table>
  );
}
